import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import ContactBrokerModal from '../components/ContactBrokerModal';
import { useLanguage } from '../context/LanguageContext';
import './AllPartnersPage.css';

const DUMMY_PARTNERS = [
  { id: 'p1', name: 'Partner 1', slug: 'partner-1', logo_url: null, banner_url: null, description: 'A leading real estate agency operating across Cyprus with over 10 years of experience helping buyers and sellers achieve their property goals.', phone: '+357 25 100 001', cities_served: ['Limassol'] },
  { id: 'p2', name: 'Partner 2', slug: 'partner-2', logo_url: null, banner_url: null, description: 'Specialising in coastal and resort properties, offering an unmatched portfolio of beachfront apartments and holiday homes across Cyprus.', phone: '+357 25 100 002', cities_served: ['Paphos'] },
  { id: 'p3', name: 'Partner 3', slug: 'partner-3', logo_url: null, banner_url: null, description: 'A trusted name in Nicosia residential property, serving both local and international buyers looking for quality homes in the capital.', phone: '+357 25 100 003', cities_served: ['Nicosia'] },
  { id: 'p4', name: 'Partner 4', slug: 'partner-4', logo_url: null, banner_url: null, description: 'Commercial and investment property specialists with a proven track record in high-yield commercial real estate across all major Cyprus cities.', phone: '+357 25 100 004', cities_served: ['Larnaca'] },
  { id: 'p5', name: 'Partner 5', slug: 'partner-5', logo_url: null, banner_url: null, description: 'New developments and off-plan specialists, connecting buyers with the best new construction projects across Cyprus at competitive prices.', phone: '+357 25 100 005', cities_served: ['Famagusta'] },
  { id: 'p6', name: 'Partner 6', slug: 'partner-6', logo_url: null, banner_url: null, description: 'Full-service agency covering residential sales, lettings and property management across Limassol and surrounding areas.', phone: '+357 25 100 006', cities_served: ['Limassol'] },
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function AllPartnersPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [contactPartner, setContactPartner] = useState(null);

  useEffect(() => {
    supabase
      .from('agencies')
      .select('id, name, slug, logo_url, description, phone, cities_served, created_at')
      .order('created_at', { ascending: true })
      .then(({ data, error }) => {
        if (error) {
          setLoadError(true);
          setLoading(false);
          return;
        }
        const real = (data || []).map(a => ({
          id:           a.id,
          name:         a.name,
          slug:         a.slug || a.id,
          logo_url:     a.logo_url,
          description:  a.description || '',
          phone:        a.phone || '',
          cities_served: a.cities_served || [],
        }));
        // Shuffle real agencies; exclude Aristo Developers from the page.
        const combined = shuffle(real.filter(p => p.name !== 'ARISTO DEVELOPERS'));
        setPartners(combined);
        setLoading(false);
      });
  }, []);

  // 10s watchdog — surface an error state if the query never resolves.
  useEffect(() => {
    setLoadTimedOut(false);
    if (!loading) return;
    const timer = setTimeout(() => setLoadTimedOut(true), 10000);
    return () => clearTimeout(timer);
  }, [loading]);

  const cities = useMemo(() => {
    const set = new Set(partners.flatMap(p => p.cities_served).filter(Boolean));
    return Array.from(set).sort();
  }, [partners]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return partners.filter(p => {
      if (cityFilter && !p.cities_served.includes(cityFilter)) return false;
      if (!term) return true;
      return [p.name, p.description, ...p.cities_served]
        .join(' ').toLowerCase().includes(term);
    });
  }, [partners, search, cityFilter]);

  return (
    <div className="ap-page">

      <div className="ap-page__hero">
        <div className="ap-page__hero-inner">
          <h1 className="ap-page__title">Verified Real Estate companies across Cyprus</h1>
          <p className="ap-page__subtitle">Browse trusted Real Estate companies — each one vetted, licensed, and ready to help you find your next home.</p>
        </div>
      </div>

      <div className="ap-page__filters">
        <div className="ap-page__filters-inner">
          <div className="ap-page__search-wrap">
            <svg className="ap-page__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              className="ap-page__search"
              type="text"
              placeholder="Search by name, city, or description..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select
            className="ap-page__select"
            value={cityFilter}
            onChange={e => setCityFilter(e.target.value)}
          >
            <option value="">All cities</option>
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {(search || cityFilter) && (
            <button className="ap-page__clear" onClick={() => { setSearch(''); setCityFilter(''); }}>
              Clear ×
            </button>
          )}
          <span className="ap-page__count">{filtered.length} {filtered.length === 1 ? 'partner' : 'partners'}</span>
        </div>
      </div>

      <div className="ap-page__body">
        {(loadError || (loading && loadTimedOut)) ? (
          <div className="partner-load-error">
            <h2>This is taking longer than expected.</h2>
            <p>Something on our end is slow to respond. Please try again.</p>
            <div className="partner-load-error__actions">
              <button className="partner-load-error__btn partner-load-error__btn--primary" onClick={() => window.location.reload()}>
                Try again
              </button>
              <button className="partner-load-error__btn partner-load-error__btn--outline" onClick={() => navigate(-1)}>
                Go back
              </button>
            </div>
          </div>
        ) : loading ? (
          <div className="ap-page__loading">Loading partners…</div>
        ) : filtered.length === 0 ? (
          <div className="ap-page__empty">
            <h3>{t('allPartners.noPartnersFound')}</h3>
            <p>Try adjusting your search or filters.</p>
          </div>
        ) : (
          <div className="ap-page__grid">
            {filtered.map(partner => (
              <article key={partner.id} className="ap-card">
                <div className="ap-card__banner">
                  {partner.banner_url || partner.logo_url ? (
                    <img
                      src={partner.banner_url || partner.logo_url}
                      alt={partner.name}
                      className="ap-card__banner-img"
                      onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div className="ap-card__banner-placeholder" style={{ display: (partner.banner_url || partner.logo_url) ? 'none' : 'flex' }}>
                    <span style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 36, color: 'rgba(184,146,74,0.5)', fontWeight: 800, letterSpacing: '-0.02em' }}>
                      {partner.name?.charAt(0) || '?'}
                    </span>
                  </div>
                </div>

                <div className="ap-card__body">
                  <h2 className="ap-card__name">{partner.name}</h2>

                  {partner.cities_served.length > 0 && (
                    <p className="ap-card__location">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      {partner.cities_served.join(', ')}
                    </p>
                  )}

                  {partner.description && (
                    <p className="ap-card__desc">{partner.description}</p>
                  )}

                  <div className="ap-card__actions">
                    <button
                      className="ap-card__btn-primary"
                      onClick={() => navigate(`/listings?company=${encodeURIComponent(partner.name)}`)}
                    >
                      View listings
                    </button>
                    <button
                      type="button"
                      className="ap-card__btn-secondary"
                      onClick={() => setContactPartner(partner)}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l.87-.87a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.79 17z"/></svg>
                      Get in touch
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <p className="ap-page__fairness">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Partners are listed in randomised order to ensure fairness, with featured partners shown first.
        </p>
      </div>

      <ContactBrokerModal
        broker={contactPartner}
        isOpen={contactPartner !== null}
        onClose={() => setContactPartner(null)}
      />
    </div>
  );
}
